import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import {
  CompetencyStatus,
  COMPETENCY_CATEGORY_LABELS,
  ExistingCompetencyForDuplicateCheck,
  findDuplicateCompetency,
  UserCompetency
} from 'app/_models/competency.model';
import { AssetTypesService } from 'app/_services/asset-types.service';
import { CompetencyService } from 'app/_services/competency.service';
import { ContractorService } from 'app/_services/contractor.service';
import { InviteUsersService } from 'app/_services/invite-users.service';
import { NotificationService } from 'app/_services/notification.service';
import { PlausibleAnalyticsService } from 'app/_services/plausible-analytics.service';
import { SiteService } from 'app/_services/site.service';
import { SiteOwnerUserService } from 'app/_services/site-owner-user.service';
import { environment } from 'environments/environment';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { clearStoredSiteOwnerSelection, getStoredSiteOwnerSelection, setStoredSiteOwnerSelection, } from 'app/_utils/site-owner-selection-storage.utils';
import { canManageCompetencyForSseRoute, hasSseCompetencyRestrictions } from 'app/_utils/competency-permissions.utils';
import { ResultsModalComponent } from './results-modal/results-modal.component';

@Component({
  selector: 'invite-users',
  templateUrl: './invite-users.component.html',
})
export class InviteUsersComponent implements OnInit {
  public pageContext = 'invite-users';
  private routeRole: any;
  public inviteForm: FormGroup;
  public addUserForm: FormGroup;
  public userList = [];

  public sitesList = [];
  private fullSitesList = [];
  private fullSitesListLoadingPromise: Promise<void> | null = null;
  private allContractorManagerSites = [];
  public assetCategoriesOptionsList = [];
  public contractorsOptionsList = [];

  public currentNumberOfSiteTypes = 0;
  private savedAssetCategoryLists = {};

  public saveInProgress = false;
  public addUserFormValidated = false;
  public emailAlreadyAddedTolist = false;
  public isPowerStationView: boolean;
  public isContractorManagerView: boolean;
  private contractorId: any;
  private isSiteOwnerView: any;
  public isAdmin: boolean;
  public siteOwnersLoading = false;
  public sitesLoading = false;
  public contractorsLoading = false;
  public showNoLinkedContractorsMessage = false;
  public assetCategoriesLoading = false;
  private pendingAssetCategoryRequests = 0;
  public selectedSiteOwner;
  public siteOwners = [];
  public isSiteDelegateView: boolean;
  private siteOwnerIds: number[] = [];
  public siteOwnerId: number;
  public addCompetencyApprovalMode: 'privileged' | 'limited';

  // Step state
  public currentStep = 1;
  public expandedUserIndex = 0;

  // Contractor name for step 2 display
  public contractorName = '';

  constructor(
    private notificationService: NotificationService,
    private siteService: SiteService,
    public assetTypesService: AssetTypesService,
    public contractorService: ContractorService,
    public inviteUsersService: InviteUsersService,
    private competencyService: CompetencyService,
    private siteOwnerUserService: SiteOwnerUserService,
    private modalService: NgbModal,
    private globals: Globals,
    fb: FormBuilder,
    private plausibleService: PlausibleAnalyticsService,
    private router: Router,
  ) {
    this.routeRole = globals.user.routeRole;
    this.isPowerStationView = this.routeRole === 'power-station-owner';
    this.isContractorManagerView = this.routeRole === 'contractor-manager';
    this.isSiteOwnerView = this.routeRole === 'site-owner';
    this.isSiteDelegateView = this.routeRole === 'site-delegate';
    this.isAdmin = this.routeRole === 'admin' || this.routeRole === 'support';
    this.addCompetencyApprovalMode = this.isContractorManagerView ? 'limited' : 'privileged';

    this.inviteForm = fb.group({
      sites: new FormControl([], [Validators.required]),
      ...(!this.isContractorManagerView && { contractor: new FormControl(undefined, [Validators.required]) }),
      guestsPermitted: new FormControl(false, [Validators.required]),
      siteSessionsPermitted: new FormControl(true, [Validators.required]),
      assetCategories: new FormControl([], [Validators.required]),
    });

    this.addUserForm = fb.group({
      firstName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      lastName: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
      email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(100)]),
      phoneNumber: new FormControl('', [Validators.required, Validators.pattern('^[^a-zA-Z]+$'), Validators.minLength(8), Validators.maxLength(30)]),
    });
  }

  ngOnInit() {
    this.initializePage();
  }

  private initializePage(): void {
    if (this.isContractorManagerView) {
      this.loadContractorManagerSiteOwnersAndSites();
      return;
    }

    // Site owner and power-station-owner each deal with a single current site owner here.
    if (this.isSiteOwnerView || this.isPowerStationView) {
      this.siteOwnerId = this.globals.siteOwnerUsers[0].skylarkUserId;
      this.selectedSiteOwner = { id: this.siteOwnerId };
      this.loadFullSitesList().then(() => {
        this.loadSitesForSelectedSiteOwner();
        this.getContractors();
      });
      return;
    }

    // Remaining path here is admin / support / site-delegate.
    this.loadFullSitesList();
    this.loadAvailableSiteOwners();
  }

  private loadAvailableSiteOwners(): void {
    if (this.isSiteDelegateView) {
      this.siteOwnerIds = this.globals.siteOwnerUsers.map((sou) => sou.skylarkUserId);

      if (this.siteOwnerIds.length === 1) {
        this.siteOwnerId = this.siteOwnerIds[0];
        this.selectedSiteOwner = { id: this.siteOwnerId };
        this.loadFullSitesList().then(() => {
          this.loadSitesForSelectedSiteOwner();
          this.getContractors();
        });
        return;
      }
    }

    // Multi-owner site-owner selection path for admin / support / site-delegate.
    if (this.isAdmin || this.isSiteDelegateView) {
      this.siteOwnersLoading = true;
      this.siteOwnerUserService.getAllEnabledSiteOwnersForDropdowns().subscribe((data: any) => {
        this.siteOwners = data
          .filter((element) => this.isAdmin || this.siteOwnerIds.includes(element.id))
          .map((element) => ({
          label: element.firstName + ' ' + element.lastName,
          value: { id: element.id }
        }));

        this.siteOwnersLoading = false;
        this.applyInitialSiteOwnerSelection();
      }, error => {
        this.siteOwnersLoading = false;
        console.error(error);
        this.notificationService.notify('danger', 'Error retrieving site owner list');
      });
    }
  }

  private loadContractorManagerSiteOwnersAndSites(): void {
    this.sitesLoading = true;
    this.contractorService.getContractorForCurrentUser().subscribe(contractor => {
      this.contractorId = contractor.id;
      this.contractorName = contractor.name || '';
      this.siteOwners = [];
      this.allContractorManagerSites = [];

      contractor.maintenanceSiteOwners
      // Temporary deny-list for site owners who do not want contractor managers
      // inviting workers directly to their sites.
        .filter((mso) => !environment.CONTRACTOR_MANAGER_INVITE_BLOCKED_SITE_OWNER_IDS.includes(mso.siteOwnerUser.skylarkUserId))
        .forEach((mso) => {
          this.siteOwners.push({
            label: mso.siteOwnerUser.name,
            value: { id: mso.siteOwnerUser.skylarkUserId }
          });

          mso.siteOwnerUser.sites
            .filter((site) => !site.deleted)
            .forEach((site) => {
              this.allContractorManagerSites.push({
                label: site.name,
                value: {
                  id: site.id,
                  name: site.name,
                  siteTypeId: site.siteTypeId,
                  siteTypeName: site.siteTypeName,
                  siteOwnerId: mso.siteOwnerUser.skylarkUserId,
                },
                siteOwnerId: mso.siteOwnerUser.skylarkUserId,
              });
            });
        });

      this.siteOwners.sort((left, right) => left.label.localeCompare(right.label));
      this.sitesLoading = false;
      this.applyInitialSiteOwnerSelection();
    }, error => {
      this.sitesLoading = false;
      console.error(error);
      this.notificationService.notify('danger', 'Error retrieving site list');
    });
  }

  private applyInitialSiteOwnerSelection(): void {
    if (!this.siteOwners.length) {
      this.clearSelectedSiteOwner();
      return;
    }

    if (this.siteOwners.length === 1) {
      this.selectedSiteOwner = this.siteOwners[0].value;
      this.siteOwnerId = this.siteOwners[0].value.id;
      if (this.isContractorManagerView) {
        this.loadSitesForSelectedSiteOwner();
        return;
      }
      this.loadFullSitesList().then(() => {
        this.loadSitesForSelectedSiteOwner();
        this.getContractors();
      });
      return;
    }

    const storedSiteOwnerId = getStoredSiteOwnerSelection(this.globals.user.id);
    const selectedSiteOwnerOption = this.siteOwners.find((option) =>
      option.value.id === storedSiteOwnerId || option.value.skylarkUserId === storedSiteOwnerId
    );

    if (selectedSiteOwnerOption) {
      this.selectedSiteOwner = selectedSiteOwnerOption.value;
      this.siteOwnerId = selectedSiteOwnerOption.value.id;
      if (this.isContractorManagerView) {
        this.loadSitesForSelectedSiteOwner();
        return;
      }
      this.loadFullSitesList().then(() => {
        this.loadSitesForSelectedSiteOwner();
        this.getContractors();
      });
      return;
    }

    this.clearSelectedSiteOwner();
  }

  private getContractors(): void {
    if (this.isContractorManagerView) {
      // contractor name already comes from the current maintenance company
      return;
    }

    if (!this.siteOwnerId) {
      this.contractorsOptionsList = [];
      this.showNoLinkedContractorsMessage = false;
      return;
    }

    if (this.isPowerStationView || this.isSiteOwnerView || this.isSiteDelegateView || this.isAdmin) {
      this.contractorsLoading = true;
      this.showNoLinkedContractorsMessage = false;
      this.contractorService.getLinkedContractors(this.siteOwnerId, ({ size: 999, page: 0 }), 'name,asc', undefined).subscribe((data: any) => {
        this.contractorsOptionsList = data.body.map((element) => ({
          label: element.name,
          value: element.id,
        }));
        this.showNoLinkedContractorsMessage = !this.contractorsOptionsList.length;
        this.contractorsLoading = false;
      }, error => {
        this.contractorsLoading = false;
        this.showNoLinkedContractorsMessage = false;
        console.error(error);
        this.notificationService.notify('danger', 'Error retrieving contractor list');
      });
      return;
    }

    // No remaining role path should reach here.
  }

  private loadSitesForSelectedSiteOwner(): void {
    if (!this.siteOwnerId) {
      this.sitesList = [];
      return;
    }

    if (this.isContractorManagerView) {
      this.applyContractorManagerSiteOwnerFilter();
      return;
    }

    // Remaining path here is admin / support / site-owner / power-station-owner / site-delegate
    this.applySiteOwnerFilterToFullSitesList();
  }

  public onSiteChanged(sites) {
    this.trackButtonClick('multi-select-sites');
    // reset list, and add options for each site type again
    this.assetCategoriesOptionsList = [];
    this.inviteForm.get('assetCategories').reset();
    this.assetCategoriesLoading = false;
    this.pendingAssetCategoryRequests = 0;

    if (!sites || !sites.length) {
      this.currentNumberOfSiteTypes = 0;
      return;
    }

    function onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    }

    let distinctSiteTypeIds = sites.map(s => s.siteTypeId).filter(onlyUnique).sort((a, b) => (a - b));
    this.pendingAssetCategoryRequests = distinctSiteTypeIds.length;
    this.assetCategoriesLoading = distinctSiteTypeIds.length > 0;

    distinctSiteTypeIds.forEach(siteTypeId => {
      this.getAssetCategories(siteTypeId);
    });
    this.currentNumberOfSiteTypes = distinctSiteTypeIds.length;
  }

  public get requiresSiteOwnerSelection(): boolean {
    return this.siteOwners.length > 1 && !this.siteOwnerId;
  }

  public get showSiteOwnerDropdown(): boolean {
    if (this.isAdmin || this.isContractorManagerView) {
      return true;
    }

    if (this.isSiteDelegateView) {
      return this.siteOwners.length > 1 || this.siteOwnerIds.length > 1 || this.siteOwnersLoading;
    }

    // site-owner / powers-station-owner
    return false;
  }

  private getAssetCategories(siteTypeId) {
    const combineCurrentOptionsListWithNew = (newList => {
      this.assetCategoriesOptionsList = [...this.assetCategoriesOptionsList, ...newList];
    });

    // return early if already fetched from backend
    if (this.savedAssetCategoryLists[siteTypeId]) {
      combineCurrentOptionsListWithNew(this.savedAssetCategoryLists[siteTypeId]);
      this.finishAssetCategoryLoad();
      return
    }

    this.assetTypesService.getAllAssetTypesForSiteType(siteTypeId).subscribe((data: any) => {
      const newList = data.map((element) => ({
        label: element.name,
        siteTypeName: element.siteType.name,
        value: element.id,
      }));
      combineCurrentOptionsListWithNew(newList);

      this.savedAssetCategoryLists[siteTypeId] = newList;
      this.finishAssetCategoryLoad();

    }, error => {
      this.finishAssetCategoryLoad();
      console.error(error);
      this.notificationService.notify('danger', 'Error retrieving asset categories');
    });
  }

  public onUserAdd() {
    this.saveInProgress = true;
    this.addUserFormValidated = false;
    this.emailAlreadyAddedTolist = false;

    if (this.addUserForm.value.email && this.userList.map(user => user.email.toLowerCase()).includes(this.addUserForm.value.email.toLowerCase().trim())) {
      this.emailAlreadyAddedTolist = true;
      this.addUserFormValidated = true;
      this.saveInProgress = false;
      return;
    }

    if (!this.addUserForm.valid) {
      this.addUserFormValidated = true;
      this.saveInProgress = false;
      return;
    }

    this.userList.push({
      firstName: this.addUserForm.value.firstName.trim(),
      lastName: this.addUserForm.value.lastName.trim(),
      email: this.addUserForm.value.email.trim(),
      phoneNumber: this.addUserForm.value.phoneNumber.trim(),
      competencies: [],
    });

    this.addUserForm.reset();
    this.saveInProgress = false;
  }

  public onUserRemove(index) {
    this.userList.splice(index, 1);
  }

  public goToStep2() {
    if (!this.inviteForm.valid || !this.userList.length) {
      return;
    }
    this.trackButtonClick('btn-go-to-step2');

    // Determine contractor name for step 2 display
    if (!this.isContractorManagerView) {
      const contractorId = this.inviteForm.value.contractor;
      const found = this.contractorsOptionsList.find(c => c.value === contractorId);
      this.contractorName = found ? found.label : '';
    }

    // Defensive fallback only; normal flow should already have a selected site owner.
    if (!this.siteOwnerId) {
      console.error('Site owner id not present where expected');
      const sites = this.inviteForm.value.sites;
      if (sites && sites.length && sites[0].siteOwnerId) {
        this.siteOwnerId = sites[0].siteOwnerId;
      }
    }

    this.expandedUserIndex = 0;
    this.currentStep = 2;
  }

  public goToStep1() {
    this.currentStep = 1;
  }

  public toggleUserExpansion(index) {
    if (this.expandedUserIndex === index) {
      this.expandedUserIndex = -1;
    } else {
      this.expandedUserIndex = index;
    }
  }

  public onCompetencyCollected(emitted: any) {
    if (!this.canCollectCompetencyOnCurrentRoute(emitted && emitted.competencyType)) {
      this.notificationService.notify('danger', 'This competency cannot be added from the current route.');
      return;
    }

    const baseComp = {
      competencyType: emitted.competencyType,
      manufacturer: emitted.manufacturer || '',
      models: emitted.models || [],
      ratings: emitted.ratings || [],
      document: emitted.document,
      expiryDate: emitted.expiryDate,
      preApprovedSelected: emitted.preApprovedSelected,
      requestEvidence: emitted.requestEvidence,
      submitForApprovalSelected: emitted.submitForApprovalSelected,
    };

    const localDuplicateMessage = this.getInviteDuplicateMessage(baseComp, !!emitted.applyToAll);
    if (localDuplicateMessage) {
      this.notificationService.notify('danger', localDuplicateMessage);
      return;
    }

    if (emitted.applyToAll) {
      this.userList.forEach(user => {
        user.competencies.push({
          competencyType: baseComp.competencyType,
          manufacturer: baseComp.manufacturer,
          models: baseComp.models.slice(),
          ratings: baseComp.ratings.slice(),
          document: baseComp.document,
          expiryDate: baseComp.expiryDate,
          preApprovedSelected: baseComp.preApprovedSelected,
          requestEvidence: baseComp.requestEvidence,
          submitForApprovalSelected: baseComp.submitForApprovalSelected,
          viewCompetency: this.buildViewCompetency(
            baseComp.competencyType,
            baseComp.manufacturer,
            baseComp.models,
            baseComp.ratings,
            baseComp.document,
            baseComp.expiryDate,
            baseComp.preApprovedSelected,
            baseComp.requestEvidence,
            baseComp.submitForApprovalSelected,
          ),
        });
      });
    } else {
      const comp: any = baseComp;
      comp.viewCompetency = this.buildViewCompetency(
        baseComp.competencyType,
        baseComp.manufacturer,
        baseComp.models,
        baseComp.ratings,
        baseComp.document,
        baseComp.expiryDate,
        baseComp.preApprovedSelected,
        baseComp.requestEvidence,
        baseComp.submitForApprovalSelected,
      );
      this.userList[this.expandedUserIndex].competencies.push(comp);
    }
  }

  private buildViewCompetency(
    competencyType: any,
    manufacturer: string,
    models: string[],
    ratings: string[],
    document: File,
    expiryDate: Date,
    preApprovedSelected: boolean,
    requestEvidence: boolean,
    submitForApprovalSelected: boolean,
  ): UserCompetency {
    const view = new UserCompetency();
    view.id = null;
    view.competencyTypeId = competencyType.id;
    view.type = COMPETENCY_CATEGORY_LABELS[competencyType.type] || competencyType.type;
    view.name = competencyType.shortName || competencyType.value;
    view.oem = manufacturer || undefined;
    view.model = models && models.length ? models.join(', ') : undefined;
    view.rating = ratings && ratings.length ? ratings.join(', ') : undefined;
    view.documentFileName = document ? document.name : undefined;
    view.expiryDate = expiryDate;

    if (preApprovedSelected) {
      view.status = CompetencyStatus.Approved;
      view.hasCompetencyApproval = true;
    } else if (submitForApprovalSelected || document) {
      view.status = CompetencyStatus.DocumentSubmitted;
      view.hasCompetencyApproval = true;
    } else if (requestEvidence) {
      view.status = CompetencyStatus.DocumentRequested;
      view.hasCompetencyApproval = true;
    } else {
      view.status = CompetencyStatus.Unverified;
      view.hasCompetencyApproval = false;
    }

    return view;
  }

  private getInviteDuplicateMessage(competency: {
    competencyType: any;
    manufacturer: string;
    models: string[];
    ratings: string[];
    document: File;
  }, applyToAll: boolean): string | null {
    const usersToCheck = applyToAll
      ? this.userList
      : [this.userList[this.expandedUserIndex]];

    const newCompetency = {
      competencyTypeId: competency.competencyType.id,
      name: competency.competencyType.value,
      isTurbine: competency.competencyType.type === 'TURBINE',
      oem: competency.manufacturer || '',
      models: competency.models || [],
      ratings: competency.ratings || [],
    };

    for (const user of usersToCheck) {
      const existingDrafts: ExistingCompetencyForDuplicateCheck[] = user && Array.isArray(user.competencies)
        ? user.competencies.map((draft) => ({
          competencyTypeId: draft.viewCompetency && draft.viewCompetency.competencyTypeId,
          status: draft.viewCompetency && draft.viewCompetency.status,
          name: draft.viewCompetency && draft.viewCompetency.name,
          oem: draft.viewCompetency && draft.viewCompetency.oem,
          model: draft.viewCompetency && draft.viewCompetency.model,
          rating: draft.viewCompetency && draft.viewCompetency.rating,
        }))
        : [];
      const conflict = findDuplicateCompetency(newCompetency, existingDrafts);

      if (!conflict) {
        continue;
      }

      return applyToAll
        ? 'This competency has already been added for one or more users in this list'
        : 'This competency has already been added for this user';
    }

    return null;
  }

  public removeCompetencyFromUser(userIndex, compIndex) {
    this.userList[userIndex].competencies.splice(compIndex, 1);
  }

  public onInviteEvidenceRequested(userIndex, compIndex) {
    this.userList[userIndex].competencies[compIndex].requestEvidence = true;
  }

  public getUserCompetencyCount(user) {
    return user.competencies ? user.competencies.length : 0;
  }

  // Save flow
  private buildInviteBody() {
    return {
      sites: this.inviteForm.value.sites.map(s => s.id),
      categories: this.inviteForm.value.assetCategories,
      maintenanceId: this.isContractorManagerView ? this.contractorId : this.inviteForm.value.contractor,
      source: environment.sourceUrl,
      workerUsers: this.userList.map(u => ({
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        phoneNumber: u.phoneNumber,
      })),
      guestsPermitted: this.inviteForm.value.guestsPermitted,
      managementDuty: this.inviteForm.value.siteSessionsPermitted,
      workOrderAdmin: false,
      competencies: [],
    };
  }

  public onSkipCompetencies() {
    this.trackButtonClick('btn-skip-competencies');
    this.saveInProgress = true;

    const body = this.buildInviteBody();

    this.inviteUsersService.sendInviteUsers(body).subscribe(response => {
      this.notificationService.notify('success', 'Invites sent');
      this.saveInProgress = false;
      this.showResultsAndNavigate(response);
    }, error => {
      console.error(error);
      this.saveInProgress = false;
      this.notificationService.notify('danger', 'Error creating the email invites');
      throw error;
    });
  }

  public onSave() {
    this.trackButtonClick('btn-save-with-competencies');
    this.saveInProgress = true;

    const body = this.buildInviteBody();

    this.inviteUsersService.sendInviteUsers(body).subscribe(response => {
      this.notificationService.notify('success', 'Invites sent');
      this.submitCompetenciesForUsers(response);
    }, error => {
      console.error(error);
      this.saveInProgress = false;
      this.notificationService.notify('danger', 'Error creating the email invites');
      throw error;
    });
  }

  private submitCompetenciesForUsers(response) {
    const allRequests = [];

    response.forEach(result => {

      const skylarkUserId = result.skylarkUserId || result.id;
      const localUser = this.userList.find(u => u.email.toLowerCase() === result.email.toLowerCase());

      if (!localUser || !localUser.competencies || !localUser.competencies.length || !skylarkUserId) {
        return;
      }

      localUser.competencies.forEach(comp => {
        const payload = {
          skylarkUserId: skylarkUserId,
          competency_type: comp.competencyType.shortName || comp.competencyType.value,
          manufacturer: comp.manufacturer || '',
          models: comp.models || [],
          ratings: comp.ratings || [],
          notes: '',
          expiry_date: this.formatDate(comp.expiryDate),
          created_by: this.globals.user.login,
          last_updated_by: this.globals.user.login,
          site_owner_id: this.siteOwnerId,
          pre_approved: comp.preApprovedSelected || false,
          request_evidence: comp.requestEvidence || false,
        };

        allRequests.push(
          this.competencyService.addCompetencyToUser(payload, comp.document)
            .pipe(catchError(err => {
              console.error('Error adding competency for ' + result.email, err);
              return of({ error: true, email: result.email, message: this.competencyService.getCompetencySubmitError(err, 'Error adding competency') });
            }))
        );
      });
    });

    if (!allRequests.length) {
      this.saveInProgress = false;
      this.showResultsAndNavigate(response);
      return;
    }

    forkJoin(allRequests).subscribe((results: any[]) => {
      const errors = results.filter(r => r && r.error);
      if (errors.length) {
        this.showInviteCompetencyErrors(errors);
      } else {
        this.notificationService.notify('success', 'Competencies saved');
      }
      this.saveInProgress = false;
      this.showResultsAndNavigate(response);
    });
  }

  private showResultsAndNavigate(response) {
    const modalOptions: NgbModalOptions = { centered: true };
    modalOptions.size = 'lg';
    const modal = this.modalService.open(ResultsModalComponent, modalOptions);
    modal.componentInstance.results = response;
    modal.componentInstance.sites = this.inviteForm.value.sites;

    const siteId = this.inviteForm.value.sites[0].id;
    const routeRole = this.routeRole;
    const router = this.router;
    const siteOwnerId = this.siteOwnerId;

    var navigateAfterModal = function() {
      if (response.length === 1 && response[0] && response[0].skylarkUserId) {
        const queryParams: any = { backLink: '/' + routeRole + '/invite-users' };
        if (siteOwnerId) {
          queryParams.siteOwnerId = siteOwnerId;
        }

        router.navigate(
          ['/' + routeRole + '/workers/' + response[0].skylarkUserId + '/manage-competencies'],
          { queryParams }
        );
      } else {
        var skylarkUserIds = response
          .map(function(r: any) { return r.skylarkUserId; })
          .filter(function(id: any) { return !!id; });
        localStorage.setItem('temp.data.invitedUsers', JSON.stringify({
          siteId: siteId,
          skylarkUserIds: skylarkUserIds,
        }));
        router.navigate(['/' + routeRole + '/sites/' + siteId + '/invited-users']);
      }
    };

    modal.result
      .then(function() { navigateAfterModal(); })
      .catch(function() { navigateAfterModal(); });
  }

  private formatDate(date) {
    if (!date) { return ''; }
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    return date;
  }

  public selectedSiteOwnerChange() {
    this.trackButtonClick('dropdown-select-site-owner');
    this.siteOwnerId = this.selectedSiteOwner.id;
    setStoredSiteOwnerSelection(this.globals.user.id, this.siteOwnerId);
    this.resetSiteSelectionState();
    if (!this.isContractorManagerView) {
      this.contractorsLoading = true;
    }

    if (this.isContractorManagerView) {
      this.loadSitesForSelectedSiteOwner();
      return;
    }

    this.loadFullSitesList().then(() => {
      this.loadSitesForSelectedSiteOwner();
      this.getContractors();
    });
  }

  private resetSiteSelectionState() {
    this.inviteForm.get('sites').reset([]);
    this.inviteForm.get('assetCategories').reset([]);
    if (!this.isContractorManagerView) {
      this.inviteForm.get('contractor').reset();
      this.contractorsOptionsList = [];
      this.showNoLinkedContractorsMessage = false;
    }
    this.assetCategoriesOptionsList = [];
    this.currentNumberOfSiteTypes = 0;
  }

  private applyContractorManagerSiteOwnerFilter() {
    this.sitesList = this.allContractorManagerSites.filter(site => site.siteOwnerId === this.siteOwnerId);
  }

  private loadFullSitesList(): Promise<void> {
    // Load the site list once and reuse it for subsequent site-owner filtering.
    if (this.fullSitesList.length) {
      return Promise.resolve();
    }

    // Already loading - reuse the first request
    if (this.fullSitesListLoadingPromise) {
      return this.fullSitesListLoadingPromise;
    }

    this.sitesLoading = true;

    this.fullSitesListLoadingPromise = new Promise<void>((resolve, reject) => {
      this.siteService.getAllSitesNoPagination().subscribe((sites) => {
        this.fullSitesList = ((sites) || []).map(x => ({
          label: x.name,
          value: {
            id: x.id,
            name: x.name,
            siteTypeName: x.siteType.name,
            siteTypeId: x.siteType.id,
            siteOwnerId: x.siteOwner ? x.siteOwner.id : null,
          },
          siteOwnerId: x.siteOwner ? x.siteOwner.id : null,
        }));

        this.sitesLoading = false;
        this.fullSitesListLoadingPromise = null;
        resolve();
      }, (error) => {
        this.sitesLoading = false;
        this.fullSitesListLoadingPromise = null;
        this.contractorsLoading = false;

        console.error(error);
        this.notificationService.notify('danger', 'Error retrieving site list');

        reject(error);
      });
    });

    return this.fullSitesListLoadingPromise;
  }

  private applySiteOwnerFilterToFullSitesList() {
    this.sitesList = this.fullSitesList.filter(site => site.siteOwnerId === this.siteOwnerId);
  }

  private clearSelectedSiteOwner(): void {
    this.siteOwnerId = null;
    this.selectedSiteOwner = null;
    this.sitesList = [];
    this.contractorsOptionsList = [];
    clearStoredSiteOwnerSelection(this.globals.user.id);
    this.resetSiteSelectionState();
  }

  private finishAssetCategoryLoad() {
    this.pendingAssetCategoryRequests = Math.max(0, this.pendingAssetCategoryRequests - 1);
    if (!this.pendingAssetCategoryRequests) {
      this.assetCategoriesLoading = false;
    }
  }

  public trackButtonClick(buttonName: string) {
    const eventName = `${buttonName}_${this.pageContext}`;
    this.plausibleService.trackButtonClick(eventName);
  }

  private showInviteCompetencyErrors(errors: any[]): void {
    const messages = Array.from(new Set(
      errors
        .map((result) => result && result.message)
        .filter((message) => !!message)
    ));

    if (messages.length === 1) {
      this.notificationService.notify('warning', 'Competency save issue', messages[0]);
      return;
    }

    this.notificationService.notify(
      'warning',
      errors.length + ' competenc' + (errors.length === 1 ? 'y' : 'ies') + ' failed to save',
      messages.length ? messages.join('<br/>') : '',
    );
  }

  private canCollectCompetencyOnCurrentRoute(competencyType: any): boolean {
    if (!hasSseCompetencyRestrictions(this.globals.user, this.siteOwnerId)) {
      return true;
    }

    return canManageCompetencyForSseRoute(
      competencyType,
      this.globals.user.canEditTurbineCompetencies,
      this.globals.user.canEditNonTurbineCompetencies,
    );
  }
}
